import React from 'react';

import { isUserSignedIn as isUserSignedInSelector } from '@peloton/auth/redux';
import { getIsBreakpointLessThan } from '@peloton/responsive/reducer';
import { useAuth } from '@auth/oauth/AuthProvider';
import { ME } from '@engage/members/models';
import { loadMember as loadMemberAction } from '@engage/members/redux';
import { getTopNavUserInfo } from '@engage/members/selectors';
import { useReduxState, useReduxAction } from '@engage/redux/hooks';
import type { SkipLinksProps } from '@members/layout/SkipLinks';
import { openModal as openModalAction } from '@members/modal/redux';
import TopNavView from './TopNavView';
const TopNav: React.FC<React.PropsWithChildren<SkipLinksProps>> = ({ skipLinks }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [timeoutId, setTimeoutId] = React.useState<null | ReturnType<typeof setTimeout>>(
    null,
  );

  const isMobile = useReduxState(getIsBreakpointLessThan, 'tabletXLarge');
  const user = useReduxState(getTopNavUserInfo);
  const hasSessionAuth = useReduxState(isUserSignedInSelector);
  const loadMember = useReduxAction(loadMemberAction);
  const openModal = useReduxAction(openModalAction);

  const isLoggedInWithSessionOrOauth = (!isLoading && isAuthenticated) || hasSessionAuth;

  React.useEffect(() => {
    if (isLoggedInWithSessionOrOauth) {
      loadMember(ME);
    }
  }, [isLoggedInWithSessionOrOauth, loadMember]);

  React.useEffect(() => {
    return () => {
      if (!!timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const handleMenuOpen = React.useCallback((e?: Event) => {
    if (e) {
      e.stopPropagation();
    }
    // Clicking open menu signifies that we want the menu animation
    window.document.body.classList.add('allow-menu-animation');
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = React.useCallback(
    (e?: Event) => {
      if (e) {
        e.stopPropagation();
      }

      // Clicking close menu signifies that we want the menu animation
      window.document.body.classList.add('allow-menu-animation');
      if (isMobile) {
        setIsMenuOpen(false);
        const timeoutRef = setTimeout(() => setIsMenuOpen(false), 250);
        setTimeoutId(timeoutRef);
      } else {
        setIsMenuOpen(false);
      }
    },
    [isMobile],
  );

  return (
    <TopNavView
      skipLinks={skipLinks}
      user={user}
      isMenuOpen={isMenuOpen}
      openModal={openModal}
      handleMenuOpen={handleMenuOpen}
      handleMenuClose={handleMenuClose}
    />
  );
};

export default TopNav;
